import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CalendarCheck,
  Check,
  ClipboardCheck,
  Edit3,
  LayoutDashboard,
  LogOut,
  RefreshCw,
  Save,
  Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../../auth/useAuth";
import AttendanceIcon from "../../../../assets/logos/Attendance Icon.png";
import ChurchLogo from "../../../../assets/logos/Church-Logo.png";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import { clearChurchOrgUserSession } from "../../services/churchOrgUserAuthService";
import { supabase } from "../../services/churchSupabaseClient";
import {
  getPresentMemberIdsForRecord,
  getTodayDate,
  loadChurchAttendanceAppWorkspace,
  saveChurchAttendanceAppRecord,
} from "../../services/churchAttendanceService";
import { eventPageStyles as styles } from "../Events/EventsPage";

const ATTENDANCE_SECTIONS = [
  { id: "overview", label: "Overview", shortLabel: "Home", icon: LayoutDashboard },
  { id: "check-in", label: "Check In", shortLabel: "Check", icon: ClipboardCheck },
  { id: "records", label: "Records", shortLabel: "Records", icon: CalendarCheck },
];

const emptyDraft = {
  id: "",
  attendanceDate: getTodayDate(),
  classCount: 0,
  kidsCount: 0,
  visitorCount: 0,
  notes: "",
  presentMemberIds: [],
};

function getWorkspaceName(user) {
  return (
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    "Church Workspace"
  );
}

function getWorkspaceLogo(user) {
  return (
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    ChurchLogo
  );
}

function getMemberName(member = {}) {
  return [member.firstName, member.lastName].filter(Boolean).join(" ") || "Unnamed member";
}

function formatDate(value = "") {
  if (!value) return "No date";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getFamilyNameMap(families = []) {
  return new Map(families.map((family) => [family.id, family.familyName || "Family"]));
}

export default function AttendancePage({ useStandaloneRoutes = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const [activeSection, setActiveSection] = useState("overview");
  const [workspace, setWorkspace] = useState({ attendance: [], families: [], members: [], checkins: [] });
  const [draft, setDraft] = useState(emptyDraft);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const workspaceName = getWorkspaceName(user);
  const organizationLogoUrl = getWorkspaceLogo(user);
  const familyNameMap = useMemo(() => getFamilyNameMap(workspace.families || []), [workspace.families]);
  const presentSet = useMemo(() => new Set(draft.presentMemberIds || []), [draft.presentMemberIds]);
  const filteredMembers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (workspace.members || [])
      .filter((member) => {
        if (!needle) return true;
        return `${getMemberName(member)} ${familyNameMap.get(member.familyId) || ""}`.toLowerCase().includes(needle);
      })
      .sort((a, b) => {
        const familyCompare = String(familyNameMap.get(a.familyId) || "").localeCompare(String(familyNameMap.get(b.familyId) || ""));
        return familyCompare || getMemberName(a).localeCompare(getMemberName(b));
      });
  }, [familyNameMap, query, workspace.members]);
  const latestRecord = workspace.attendance?.[0] || null;
  const checkedMemberCount = draft.presentMemberIds.length;
  const worshipCount = checkedMemberCount + Number(draft.visitorCount || 0);
  const averageWorship = Math.round(
    (workspace.attendance || []).reduce((sum, record) => sum + Number(record.worshipCount || 0), 0) /
      Math.max((workspace.attendance || []).length, 1)
  );

  async function refreshWorkspace(options = {}) {
    if (options.showLoading !== false) setLoading(true);
    setError("");

    try {
      const nextWorkspace = await loadChurchAttendanceAppWorkspace(user);
      setWorkspace(nextWorkspace);
      if (options.showNotice) setNotice("Attendance refreshed.");
    } catch (loadError) {
      console.error("Attendance load error:", loadError);
      setError(loadError?.message || "Could not load attendance.");
    } finally {
      if (options.showLoading !== false) setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function hydrate() {
      setLoading(true);
      try {
        const nextWorkspace = await loadChurchAttendanceAppWorkspace(user);
        if (mounted) setWorkspace(nextWorkspace);
      } catch (loadError) {
        console.error("Attendance load error:", loadError);
        if (mounted) setError(loadError?.message || "Could not load attendance.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    hydrate();

    return () => {
      mounted = false;
    };
  }, [user]);

  function resetDraft() {
    setDraft({
      ...emptyDraft,
      attendanceDate: getTodayDate(),
      presentMemberIds: [],
    });
    setQuery("");
  }

  function editRecord(record) {
    setDraft({
      id: record.id,
      attendanceDate: record.attendanceDate || getTodayDate(),
      classCount: record.classCount || 0,
      kidsCount: record.kidsCount || 0,
      visitorCount: record.visitorCount || 0,
      notes: record.notes || "",
      presentMemberIds: getPresentMemberIdsForRecord(workspace, record.id),
    });
    setActiveSection("check-in");
    setNotice("");
    setError("");
  }

  function toggleMember(memberId) {
    setDraft((current) => {
      const present = new Set(current.presentMemberIds || []);
      if (present.has(memberId)) {
        present.delete(memberId);
      } else {
        present.add(memberId);
      }
      return { ...current, presentMemberIds: Array.from(present) };
    });
  }

  function markAllVisible() {
    setDraft((current) => {
      const present = new Set(current.presentMemberIds || []);
      filteredMembers.forEach((member) => present.add(member.id));
      return { ...current, presentMemberIds: Array.from(present) };
    });
  }

  function clearVisible() {
    setDraft((current) => {
      const visible = new Set(filteredMembers.map((member) => member.id));
      return {
        ...current,
        presentMemberIds: (current.presentMemberIds || []).filter((memberId) => !visible.has(memberId)),
      };
    });
  }

  async function handleSave(event) {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    setNotice("");
    setError("");

    try {
      const nextWorkspace = await saveChurchAttendanceAppRecord(user, {
        ...draft,
        classCount: 0,
        kidsCount: 0,
        worshipCount,
      });
      setWorkspace(nextWorkspace);
      resetDraft();
      setActiveSection("records");
      setNotice("Attendance saved.");
    } catch (saveError) {
      console.error("Attendance save error:", saveError);
      setError(saveError?.message || "Could not save attendance.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    clearChurchOrgUserSession();
    await supabase.auth.signOut();
    navigate("/church/login", { replace: true });
  }

  if (loading) {
    return (
      <PanelLoadingState
        iconAlt="Attendance"
        iconSrc={AttendanceIcon}
        title="Loading Attendance"
        detail="Preparing member check-in and attendance records..."
      />
    );
  }

  const showOverview = !useStandaloneRoutes || activeSection === "overview";
  const showCheckIn = !useStandaloneRoutes || activeSection === "check-in";
  const showRecords = !useStandaloneRoutes || activeSection === "records";

  return (
    <div style={useStandaloneRoutes ? { ...styles.wrapper, ...(isPhone ? styles.wrapperPhone : {}) } : styles.page}>
      <div
        style={
          useStandaloneRoutes
            ? { ...styles.managementHeader, ...(isPhone ? styles.managementHeaderPhone : {}) }
            : { ...styles.pageHeader, ...(isPhone ? styles.pageHeaderPhone : {}) }
        }
      >
        {useStandaloneRoutes ? (
          <>
            <span aria-hidden="true" style={styles.managementHeaderShapeOne} />
            <span aria-hidden="true" style={styles.managementHeaderShapeTwo} />
            <div style={{ ...styles.managementHeaderMain, ...(isPhone ? styles.managementHeaderMainPhone : {}) }}>
              <img src={AttendanceIcon} alt="Attendance" style={styles.managementHeaderIcon} />
              <div style={styles.managementHeaderCopy}>
                <div style={styles.managementHeaderEyebrow}>Oikos Church Attendance</div>
                <div style={styles.managementHeaderName}>{workspaceName}</div>
              </div>
            </div>
            <div style={{ ...styles.managementHeaderMeta, ...(isPhone ? styles.managementHeaderMetaPhone : {}) }}>
              <div style={styles.managementHeaderWelcomeLabel}>Today</div>
              <div style={styles.managementHeaderUserName}>{checkedMemberCount} members checked</div>
              <button
                type="button"
                style={{ ...styles.managementHeaderIdRow, ...(isPhone ? styles.managementHeaderIdRowPhone : {}) }}
                onClick={() => refreshWorkspace({ showNotice: true })}
              >
                <RefreshCw size={15} />
                <span>Refresh</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <div style={styles.pageKicker}>Attendance</div>
              <h1 style={styles.pageTitle}>Member Check-In</h1>
              <div style={styles.pageMeta}>{workspaceName}</div>
            </div>
            <button type="button" style={styles.ghostButton} onClick={() => refreshWorkspace({ showNotice: true })}>
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </>
        )}
      </div>

      {notice ? <div style={styles.notice}>{notice}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      <div style={useStandaloneRoutes ? styles.eventShell : styles.embeddedShell}>
        {useStandaloneRoutes ? (
          <aside style={{ ...styles.sideNav, ...(isPhone ? styles.sideNavPhone : {}) }} aria-label="Attendance sections">
            <div style={{ ...styles.sideNavBrand, ...(isPhone ? styles.sideNavBrandPhone : {}) }}>
              <img src={ChurchLogo} alt="Oikos Church" style={styles.sideNavBrandLogo} />
              <div style={styles.sideNavBrandLine} />
            </div>
            <div style={{ ...styles.sideNavOrg, ...(isPhone ? styles.sideNavOrgPhone : {}) }}>
              <img src={organizationLogoUrl} alt={workspaceName} style={styles.sideNavLogo} />
              <div style={styles.sideNavOrgCopy}>
                <span style={styles.sideNavOrgLabel}>Organization</span>
                <strong style={styles.sideNavOrgName}>{workspaceName}</strong>
              </div>
            </div>
            <div style={{ ...styles.sideNavDivider, ...(isPhone ? styles.sideNavDividerPhone : {}) }} />
            <div style={{ ...styles.sideNavTitle, ...(isPhone ? styles.sideNavTitlePhone : {}) }}>Attendance</div>
            <div style={{ ...styles.navMain, ...(isPhone ? styles.navMainPhone : {}) }}>
              {ATTENDANCE_SECTIONS.map((item) => {
                const Icon = item.icon;
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    style={{
                      ...styles.navButton,
                      ...(isPhone ? styles.navButtonPhone : {}),
                      ...(active ? { ...styles.navButtonActive, background: "var(--color-primary)" } : {}),
                      ...(isPhone && active ? styles.navButtonActivePhone : {}),
                    }}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <Icon size={isPhone ? 15 : 17} />
                    <span style={isPhone ? styles.navLabelPhone : null}>{isPhone ? item.shortLabel : item.label}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ ...styles.sideNavFooter, ...(isPhone ? styles.sideNavFooterPhone : {}) }}>
              <button type="button" style={{ ...styles.footerButton, ...(isPhone ? styles.footerButtonPhone : {}) }} onClick={() => navigate("/church")}>
                <ArrowLeft size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Apps</span>
              </button>
              <button type="button" style={{ ...styles.footerButton, ...styles.logoutButton, ...(isPhone ? styles.footerButtonPhone : {}) }} onClick={handleLogout}>
                <LogOut size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Logout</span>
              </button>
            </div>
          </aside>
        ) : null}

        <div style={useStandaloneRoutes ? { ...styles.content, ...(isPhone ? styles.contentPhone : {}) } : styles.embeddedContent}>
          {showOverview ? (
            <section style={styles.sectionGrid}>
              <div style={styles.heroPanel}>
                <div style={styles.heroCopy}>
                  <div style={styles.heroKicker}>Member check-in</div>
                  <h1 style={styles.heroTitle}>Take attendance from the Church app.</h1>
                  <p style={styles.heroText}>Check members present, add visitor counts, and save the weekly record.</p>
                  <div style={styles.heroActions}>
                    <button type="button" style={styles.primaryButton} onClick={() => setActiveSection("check-in")}>
                      <ClipboardCheck size={17} />
                      <span>Start Check-In</span>
                    </button>
                    <button type="button" style={styles.ghostButton} onClick={() => refreshWorkspace({ showNotice: true })}>
                      <RefreshCw size={16} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
              </div>

              <div style={attendanceStyles.overviewTileGrid}>
                <div style={attendanceStyles.latestSundayTile}>
                  <div>
                    <div style={styles.statLabel}>Latest Sunday</div>
                    <div style={attendanceStyles.latestSundayDate}>
                      {latestRecord ? formatDate(latestRecord.attendanceDate) : "No record yet"}
                    </div>
                  </div>
                  <div style={attendanceStyles.latestSundayCounts}>
                    <div>
                      <span style={attendanceStyles.latestSundayNumber}>{latestRecord?.worshipCount || 0}</span>
                      <span style={attendanceStyles.latestSundayLabel}>Worship</span>
                    </div>
                    <div>
                      <span style={attendanceStyles.latestSundayNumber}>{latestRecord?.visitorCount || 0}</span>
                      <span style={attendanceStyles.latestSundayLabel}>Visitors</span>
                    </div>
                  </div>
                </div>
                <StatCard label="Members" value={(workspace.members || []).length} />
                <StatCard label="Checked In" value={checkedMemberCount} tone="green" />
                <StatCard label="Average Worship" value={averageWorship} tone="gray" />
              </div>
            </section>
          ) : null}

          {showCheckIn ? (
            <form style={styles.panel} onSubmit={handleSave}>
              <div style={styles.panelHeader}>
                <div>
                  <div style={styles.panelTitle}>{draft.id ? "Edit Attendance" : "Check In Members"}</div>
                  <div style={styles.panelSubtle}>{checkedMemberCount} members + {Number(draft.visitorCount || 0)} visitors = {worshipCount} worship total</div>
                </div>
                <button type="submit" style={styles.primaryButton} disabled={saving}>
                  <Save size={16} />
                  <span>{saving ? "Saving..." : "Save Attendance"}</span>
                </button>
              </div>

              <div style={styles.formGrid}>
                <Field label="Date" type="date" value={draft.attendanceDate} onChange={(value) => setDraft((current) => ({ ...current, attendanceDate: value }))} />
                <Field label="Visitors" type="number" value={draft.visitorCount} onChange={(value) => setDraft((current) => ({ ...current, visitorCount: value }))} />
              </div>

              <label style={attendanceStyles.notesWrap}>
                <span style={attendanceStyles.label}>Notes</span>
                <textarea
                  style={attendanceStyles.textarea}
                  value={draft.notes}
                  onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
                  rows={3}
                />
              </label>

              <div style={attendanceStyles.checkToolbar}>
                <label style={attendanceStyles.searchWrap}>
                  <Search size={16} />
                  <input
                    style={attendanceStyles.searchInput}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search members or families"
                  />
                </label>
                <button type="button" style={styles.ghostButton} onClick={markAllVisible}>Check Visible</button>
                <button type="button" style={styles.ghostButton} onClick={clearVisible}>Clear Visible</button>
              </div>

              <div style={attendanceStyles.memberGrid}>
                {filteredMembers.map((member) => {
                  const isPresent = presentSet.has(member.id);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      style={{ ...attendanceStyles.memberButton, ...(isPresent ? attendanceStyles.memberButtonChecked : {}) }}
                      onClick={() => toggleMember(member.id)}
                    >
                      <span style={{ ...attendanceStyles.checkBox, ...(isPresent ? attendanceStyles.checkBoxOn : {}) }}>
                        {isPresent ? <Check size={14} /> : null}
                      </span>
                      <span style={attendanceStyles.memberCopy}>
                        <span style={attendanceStyles.memberName}>{getMemberName(member)}</span>
                        <span style={attendanceStyles.familyName}>{familyNameMap.get(member.familyId) || "No family"}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {filteredMembers.length === 0 ? <div style={styles.emptyState}>No members found.</div> : null}
            </form>
          ) : null}

          {showRecords ? (
            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <div>
                  <div style={styles.panelTitle}>Attendance Records</div>
                  <div style={styles.panelSubtle}>Saved weekly attendance from the Church app</div>
                </div>
              </div>
              <div style={styles.cardList}>
                {(workspace.attendance || []).map((record) => (
                  <article key={record.id} style={styles.eventCard}>
                    <div style={styles.eventMain}>
                      <div style={{ ...styles.statusDot, ...styles.statusDotLive }} />
                      <div style={styles.eventCopy}>
                        <div style={styles.eventTitleRow}>
                          <div style={styles.eventTitle}>{formatDate(record.attendanceDate)}</div>
                        </div>
                        <div style={styles.eventMeta}>Worship {record.worshipCount} • Visitors {record.visitorCount}</div>
                        {record.notes ? <div style={styles.eventText}>{record.notes}</div> : null}
                      </div>
                    </div>
                    <div style={styles.cardActions}>
                      <button type="button" style={styles.iconButton} onClick={() => editRecord(record)}>
                        <Edit3 size={15} />
                        <span>Edit</span>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
              {!(workspace.attendance || []).length ? <div style={styles.emptyState}>No attendance records yet.</div> : null}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, onChange, type = "text", value }) {
  return (
    <label style={attendanceStyles.field}>
      <span style={attendanceStyles.label}>{label}</span>
      <input style={attendanceStyles.input} type={type} value={value} onChange={(event) => onChange(event.target.value)} min={type === "number" ? "0" : undefined} />
    </label>
  );
}

function StatCard({ label, value, tone = "blue" }) {
  return (
    <div style={{ ...styles.statCard, ...(tone === "green" ? styles.statGreen : tone === "gray" ? styles.statGray : {}) }}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

const attendanceStyles = {
  overviewTileGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  },
  latestSundayTile: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 18,
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "space-between",
    minHeight: 112,
    padding: 16,
  },
  latestSundayDate: {
    color: "#0f172a",
    fontSize: 20,
    fontWeight: 950,
    lineHeight: 1.15,
    marginTop: 8,
  },
  latestSundayCounts: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(2, minmax(74px, 1fr))",
    textAlign: "right",
  },
  latestSundayNumber: {
    color: "#0f172a",
    display: "block",
    fontSize: 30,
    fontWeight: 950,
    lineHeight: 1,
  },
  latestSundayLabel: {
    color: "#64748b",
    display: "block",
    fontSize: 12,
    fontWeight: 900,
    marginTop: 5,
    textTransform: "uppercase",
  },
  field: {
    display: "grid",
    gap: 7,
  },
  label: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  input: {
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 8,
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 800,
    padding: "11px 12px",
  },
  notesWrap: {
    display: "grid",
    gap: 7,
    marginTop: 14,
  },
  textarea: {
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 8,
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.45,
    padding: "11px 12px",
    resize: "vertical",
  },
  checkToolbar: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 18,
  },
  searchWrap: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 8,
    color: "#64748b",
    display: "flex",
    flex: "1 1 240px",
    gap: 8,
    padding: "0 12px",
  },
  searchInput: {
    background: "transparent",
    border: 0,
    color: "#0f172a",
    flex: 1,
    fontSize: 14,
    fontWeight: 800,
    minHeight: 42,
    minWidth: 0,
    outline: "none",
  },
  memberGrid: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    marginTop: 16,
  },
  memberButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 8,
    color: "#0f172a",
    cursor: "pointer",
    display: "flex",
    gap: 10,
    minHeight: 62,
    padding: 12,
    textAlign: "left",
  },
  memberButtonChecked: {
    background: "#ecf7ef",
    borderColor: "#bfe4c8",
  },
  checkBox: {
    alignItems: "center",
    border: "2px solid #cbd5e1",
    borderRadius: 8,
    color: "#ffffff",
    display: "flex",
    height: 26,
    justifyContent: "center",
    width: 26,
  },
  checkBoxOn: {
    background: "#166534",
    borderColor: "#166534",
  },
  memberCopy: {
    display: "grid",
    gap: 3,
    minWidth: 0,
  },
  memberName: {
    fontSize: 14,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },
  familyName: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    overflowWrap: "anywhere",
  },
};
